import { useRef, useState } from 'react';
import { Paperclip, X, Loader2, FileText } from 'lucide-react';
import { api } from '../lib/api';

interface UploadedFile {
  filename: string;
  originalName: string;
  size: number;
}

interface FileUploaderProps {
  sessionId: string;
  disabled?: boolean;
  onUploaded?: (file: UploadedFile) => void;
  onError: (message: string) => void;
}

export function FileUploader({ sessionId, disabled, onUploaded, onError }: FileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState<UploadedFile[]>([]);

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const res = await api.uploadFile(sessionId, file);
        const uploaded: UploadedFile = {
          filename: res.file.filename,
          originalName: res.file.originalName,
          size: res.file.size,
        };
        setRecent((prev) => [...prev, uploaded]);
        onUploaded?.(uploaded);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeRecent = (idx: number) => {
    setRecent((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled || uploading}
        className="btn"
        title="Anexar arquivos (será injetado como contexto na próxima mensagem)"
      >
        {uploading ? (
          <Loader2 size={14} className="animate-spin" />
        ) : (
          <Paperclip size={14} />
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={handleSelect}
        className="hidden"
      />
      {recent.length > 0 && (
        <div className="absolute bottom-full left-0 right-0 mb-2 px-4 flex flex-wrap gap-1">
          {recent.map((f, i) => (
            <span
              key={`${f.filename}-${i}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-bg-card border border-border text-xs"
            >
              <FileText size={11} className="text-blue-600" />
              <span className="max-w-[150px] truncate">{f.originalName}</span>
              <span className="text-text-muted">({(f.size / 1024).toFixed(1)} KB)</span>
              <button
                type="button"
                onClick={() => removeRecent(i)}
                className="text-text-muted hover:text-red-600"
                title="Remover do contexto local"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
    </>
  );
}
